export const openInNewTab = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

const ADDIN_HOST = 'addin';

export enum WindowNameEnum {
  TEAMS = 'TEAMS',
  ADDIN = 'ADDIN',
  WEBAPP = 'WEBAPP',
}

const isWindowNameEnum = (value: string): value is WindowNameEnum =>
  (Object.values(WindowNameEnum) as string[]).includes(value);

// Belt-and-suspenders companion to window.name for the Teams "App" tab's cross-origin redirect
// to the webapp (see apps/teams/src/tabs/LegacyApp): some browsers reset window.name on a
// cross-origin navigation as an anti-tracking measure, which would otherwise make the webapp
// fall back to detecting itself as a normal top-level WEBAPP (wrong cookie SameSite/Partitioned
// options) even though it's still embedded as a 3rd-party Teams iframe. Appending this to the
// redirect URL gives the destination an origin-independent signal that doesn't rely on
// window.name surviving the navigation.
export const TEAMS_CONTEXT_QUERY_PARAM = 'jxtCtx';
export const TEAMS_CONTEXT_QUERY_VALUE = 'teams';

/**
 * Sets `window.name` to the given app identifier, but only when it isn't already set (undefined,
 * null or ''). Each app calls this once at startup so `getWindowName()` has an authoritative
 * value — including after a same-frame navigation, where `window.name` persists (e.g. the Teams
 * "App" tab redirecting its own frame to the webapp). The Teams host sets `window.name` itself
 * ('embedded-page-container'), which is truthy, so this never overwrites it — `getWindowName()`
 * still resolves that to TEAMS via its fallback.
 */
export const setWindowName = (name: WindowNameEnum): void => {
  if (!window.name) {
    window.name = name;
  }
};

export const getWindowName = (): WindowNameEnum => {
  // Explicitly set by the app at startup (see setWindowName) — authoritative when present.
  if (isWindowNameEnum(window.name)) {
    return window.name;
  }

  // Fallbacks for when window.name is something else: the Teams host's own values, before
  // setWindowName has run, the URL marker a cross-origin redirect from the Teams "App" tab
  // appends (see TEAMS_CONTEXT_QUERY_PARAM, for when the browser reset window.name during that
  // navigation), or a standalone addin identified only by hostname.
  // Teams app within teams: "embedded-page-container"
  // Teams app within outlook (yes this is possible): "embedded-page-container_xxx"
  if (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.parent === window.self && (window as any).nativeInterface) ||
    window.name.startsWith('embedded-page-container') ||
    window.name === 'extension-tab-frame' ||
    new URLSearchParams(window.location.search).get(TEAMS_CONTEXT_QUERY_PARAM) === TEAMS_CONTEXT_QUERY_VALUE
  ) {
    return WindowNameEnum.TEAMS;
  }

  if (window.location.hostname.includes(ADDIN_HOST)) {
    return WindowNameEnum.ADDIN;
  }

  return WindowNameEnum.WEBAPP;
};
